import { LazyQueryExecFunction, OperationVariables } from '@apollo/client';
import { TextFieldVariants, Autocomplete, TextField, CircularProgress } from '@mui/material';
import React, { useMemo } from 'react';

export type AutoCompleteOption = { name: string; _id: string; referenceId?: string };

type AutocompleteWithFetchProps = {
  fetch: LazyQueryExecFunction<any, OperationVariables> | (() => void);
  label: string;
  handleChange: (arg: AutoCompleteOption | null) => void;
  disabled?: boolean;
  loading: boolean;
  variables?: any;
  required?: boolean;
  labelWithId?: boolean;
  size?: 'medium' | 'small';
  error?: boolean;
  noOptionText?: string;
  onBlur?: () => void;
  variant?: TextFieldVariants;
  addNewConfig?: {
    enableAddNew: boolean;
    onClick: () => void;
    addNewLabel?: string;
  };
} & (
  | {
      value: AutoCompleteOption;
      options: AutoCompleteOption[];
    }
  | {
      value: string;
      options: string[];
    }
);

const AutocompleteWithFetch: React.FC<AutocompleteWithFetchProps> = ({
  value,
  label,
  handleChange,
  disabled = false,
  required = false,
  variables = {},
  labelWithId = false,
  error = false,
  onBlur,
  addNewConfig = {
    enableAddNew: false,
    onClick: () => {},
  },
  size = 'medium',
  variant = 'outlined',
  ...props
}) => {
  const [open, setOpen] = React.useState(false);
  const addNewOptionId = 'add-new';

  const transformedOptions = useMemo(() => {
    const transformed: AutoCompleteOption[] = [];

    if (Array.isArray(props.options) && props.options.length > 0) {
      if (typeof props.options[0] === 'string') {
        transformed.push(
          ...props.options.map(option => ({
            name: option,
            _id: option,
          }))
        );
      } else {
        transformed.push(...(props.options as AutoCompleteOption[]));
      }
    }

    if (addNewConfig.enableAddNew && !transformed.some(option => option?._id === addNewOptionId)) {
      const addNewLabel = addNewConfig.addNewLabel ?? '+ Add New';
      transformed.push({ name: addNewLabel, _id: addNewOptionId });
    }

    return transformed;
  }, [props.options, addNewConfig.enableAddNew, addNewConfig.addNewLabel]);

  return (
    <>
      <Autocomplete
        open={open}
        onOpen={() => {
          props.fetch({
            variables,
          });
          setOpen(true);
        }}
        onClose={() => setOpen(false)}
        disabled={disabled}
        value={typeof value === 'string' ? { _id: value, name: value, referenceId: value } : value}
        getOptionLabel={option => {
          if (!!option && !!!option._id) return '';

          if (labelWithId && !!option.referenceId) {
            return `${option.name} (${option.referenceId})`;
          }
          return option.name;
        }}
        isOptionEqualToValue={(option, value) => option?._id === value?._id}
        fullWidth
        includeInputInList
        onChange={(e, val) => {
          if (val && val._id === addNewOptionId) {
            addNewConfig.onClick();
          } else {
            handleChange(val ? val : { name: '', _id: '', referenceId: '' });
          }
        }}
        options={transformedOptions}
        loading={props.loading}
        noOptionsText={props.noOptionText}
        renderInput={params => (
          <TextField
            required={required}
            variant={variant}
            {...params}
            error={error}
            size={size}
            onBlur={onBlur ? () => onBlur() : undefined}
            label={label}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {props.loading && open ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    </>
  );
};

export default AutocompleteWithFetch;
